import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useUser } from '@/queries/useUser';
import { useSubscriptionPrices } from '@/hooks/useSubscriptionPrices';
import { useDuplicatePaymentCheck } from '@/features/subscriptions/hooks/useDuplicatePaymentCheck';
import { createSubscription } from '@/lib/api/subscriptions';
import { getCustomerId } from '@/lib/api/stripe';
import { stanNotify } from '@/lib/stanNotify';
import { getUUID } from '@/lib/helpers';
import {
    trackPaymentMethodCreated,
    trackSubscriptionCreating,
    trackSubscriptionFailed,
    trackSubscriptionSuccess,
    trackTermsAgreed,
} from '@/features/subscriptions/logic/subscriptionAnalytics.logic';
import type { StripePaymentInputRef } from '@/components/third-party/StripePaymentInput/StripePaymentInput';

const TRIAL_DAYS = 14;

interface UseStartTrialModalProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Hook for handling affiliate trial subscription creation
 * Follows the same pattern as useSubscriptionCreation but simplified for the modal context
 */
export const useStartTrialModal = ({ isOpen, onClose }: UseStartTrialModalProps) => {
    const { push } = useStanRouter();
    const { user, refetchUser } = useUser();
    const { currency, creatorMonthlyPrice, creatorMonthlyPriceId } = useSubscriptionPrices();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [termsAgreed, setTermsAgreed] = useState(false);

    const customerIdRef = useRef<string | null>(null);
    const idempotencyKeyRef = useRef<string>(getUUID());

    const hasPastSubscription = user?.has_subscription_history ?? false;

    // Memoize userMeta to prevent infinite loops in useEffect dependencies
    const userMeta = useMemo(
        () => ({
            user_id: user?.user_id,
            username: user?.username,
            email: user?.email,
            full_name: user?.full_name,
        }),
        [user?.user_id, user?.username, user?.email, user?.full_name]
    );

    const planInfo = useMemo(
        () => ({
            price: creatorMonthlyPrice,
            currency,
            planName: 'Creator Monthly',
            isYearly: false,
        }),
        [creatorMonthlyPrice, currency]
    );

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setTermsAgreed(false);
            setIsSubmitting(false);
            idempotencyKeyRef.current = getUUID();
        }
    }, [isOpen]);

    const clearSubscriptionData = useCallback(() => {
        [null, 'stanwith.me', 'stan.store'].forEach(domain => {
            document.cookie = `page=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;${domain ? ` domain=${domain};` : ''}`;
        });
    }, []);

    const preventBackButton = useCallback((url: string) => {
        for (let i = 0; i < 10; i++) window.history.pushState({}, String(i), url);
    }, []);

    const createSubscriptionWithPaymentMethod = useCallback(
        async (paymentMethodId: string) => {
            if (!customerIdRef.current || !creatorMonthlyPriceId) {
                stanNotify({
                    type: 'error',
                    title: 'Something went wrong',
                    text: 'Please reach out to us at friends@stanwith.me.',
                });
                setIsSubmitting(false);
                return;
            }

            trackSubscriptionCreating(userMeta);

            try {
                const response = await createSubscription({
                    customerId: customerIdRef.current,
                    priceId: creatorMonthlyPriceId,
                    trialDays: TRIAL_DAYS,
                    mustApplyTrial: true,
                    idempotencyKey: idempotencyKeyRef.current,
                    paymentMethodId,
                });

                if (response.status === 'active' || response.status === 'trialing') {
                    await refetchUser();
                    trackSubscriptionSuccess(userMeta, planInfo);
                    clearSubscriptionData();

                    onClose();

                    // Navigate to onboarding like Vue does
                    preventBackButton('/onboarding');
                    push('/onboarding');
                } else {
                    stanNotify({
                        type: 'error',
                        title: 'Something went wrong',
                        text: 'Please reach out to us at friends@stanwith.me.',
                    });
                    setIsSubmitting(false);
                }
            } catch (error: unknown) {
                const errorMessage =
                    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                    'Please try again later.';
                stanNotify({ type: 'error', title: "Couldn't create a subscription", text: errorMessage });
                trackSubscriptionFailed(userMeta, errorMessage);
                setIsSubmitting(false);
            }
        },
        [
            creatorMonthlyPriceId,
            userMeta,
            planInfo,
            refetchUser,
            onClose,
            push,
            clearSubscriptionData,
            preventBackButton,
        ]
    );

    // Duplicate payment method check hook
    const duplicatePaymentCheck = useDuplicatePaymentCheck({
        userMeta,
        hasPastSubscription,
        onConfirm: createSubscriptionWithPaymentMethod,
    });

    const handleSubmit = useCallback(
        async (stripeRef: React.RefObject<StripePaymentInputRef | null>) => {
            if (!termsAgreed) return;

            setIsSubmitting(true);
            trackTermsAgreed(userMeta);

            try {
                // Get customer ID if not already fetched
                if (!customerIdRef.current) {
                    customerIdRef.current = await getCustomerId();
                }

                // Create payment method
                if (!stripeRef.current) {
                    setIsSubmitting(false);
                    return;
                }

                const paymentMethodId = await stripeRef.current.createPaymentMethod(
                    customerIdRef.current,
                    user?.full_name || '',
                    user?.email || ''
                );

                if (!paymentMethodId) {
                    setIsSubmitting(false);
                    return;
                }

                trackPaymentMethodCreated(userMeta, planInfo);

                // Check for duplicate payment method
                const hasDuplicates = await duplicatePaymentCheck.checkForDuplicates(paymentMethodId);
                if (hasDuplicates) {
                    // Modal is shown, wait for user action
                    return;
                }

                await createSubscriptionWithPaymentMethod(paymentMethodId);
            } catch (error: unknown) {
                const errorMessage =
                    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                    'Please try again later.';
                stanNotify({ type: 'error', title: "Couldn't create a subscription", text: errorMessage });
                trackSubscriptionFailed(userMeta, errorMessage);
                setIsSubmitting(false);
            }
        },
        [termsAgreed, user, userMeta, planInfo, createSubscriptionWithPaymentMethod, duplicatePaymentCheck]
    );

    return {
        isSubmitting,
        termsAgreed,
        setTermsAgreed,
        trialDays: TRIAL_DAYS,
        handleSubmit,
        duplicatePaymentCheck,
    };
};
